import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { rupees } from "../rates/rateModel";
import { useKT } from "../i18n/kt";

/** What happens with the money actually handed over: extra becomes an advance, short leaves the rest due. */
export const handOverOutcome = (netDue: number, given: number): { kind: "EXACT" | "MORE" | "LESS"; diff: number } => {
  const diff = Math.round((given - netDue) * 100) / 100;
  if (Math.abs(diff) < 0.005) return { kind: "EXACT", diff: 0 };
  return diff > 0 ? { kind: "MORE", diff } : { kind: "LESS", diff: -diff };
};

/**
 * Pay one person from the pay sheet: the supervisor types what they actually gave. More than due →
 * the extra is booked as an advance (with its cut per payout); less → it's a part payment and the
 * rest stays due. Nothing is guessed — the outcome is spelled out before saving.
 */
const HandOverDialog = ({
  line,
  busy,
  onCancel,
  onPay,
}: {
  line: any | null;
  busy: boolean;
  onCancel: () => void;
  onPay: (body: { paid: string; cut: string; method: string; recoveryPerPay?: string }) => void;
}) => {
  const { t } = useKT();
  const [given, setGiven] = useState("");
  const [cut, setCut] = useState("");
  const [method, setMethod] = useState("CASH");
  const [perPay, setPerPay] = useState("");

  useEffect(() => {
    if (!line) return;
    setGiven(String(Number(line.netDue)));
    setCut(String(Number(line.cut || 0)));
    setMethod("CASH");
    setPerPay("");
  }, [line]);

  if (!line) return null;
  const due = Number(line.wagesDue || 0);
  const cutN = Number(cut || 0);
  const net = Math.max(0, Math.round((due - cutN) * 100) / 100);
  const givenN = Number(given || 0);
  const out = handOverOutcome(net, givenN);
  const valid = given.trim() !== "" && givenN >= 0 && cutN >= 0 && cutN <= Math.max(0, Math.min(due, Number(line.advanceOwed || 0)));

  return (
    <Modal isOpen={!!line} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        {t("Pay")} {line.code} {line.name}
      </ModalHeader>
      <ModalBody>
        <div className="kho-sum">
          <span>{t("Wages due")}</span>
          <strong>{rupees(due)}</strong>
        </div>
        {Number(line.advanceOwed) > 0 ? (
          <>
            <label className="kr-label" htmlFor="kho-cut">
              {t("Cut for the advance (owes {{amount}})", { amount: rupees(line.advanceOwed) })}
            </label>
            <input id="kho-cut" className="kr-select" inputMode="decimal" value={cut} onChange={(e) => setCut(e.target.value.replace(/[^\d.]/g, ""))} />
          </>
        ) : null}
        <div className="kho-sum">
          <span>{t("To pay now")}</span>
          <strong>{rupees(net)}</strong>
        </div>
        <label className="kr-label" htmlFor="kho-given">
          {t("Actually handed over")}
        </label>
        <input id="kho-given" className="kr-select kho-given" inputMode="decimal" value={given} onChange={(e) => setGiven(e.target.value.replace(/[^\d.]/g, ""))} autoFocus />
        <div className="kr-chips mt-2" role="radiogroup" aria-label={t("Given as")}>
          {[
            ["CASH", t("Cash")],
            ["UPI", "UPI"],
            ["BANK", t("Bank")],
          ].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={method === k} className={`kr-choice${method === k ? " is-on" : ""}`} onClick={() => setMethod(k)}>
              {l}
            </button>
          ))}
        </div>
        <p className={`kho-out is-${out.kind}`}>
          {out.kind === "EXACT"
            ? t("Paid in full.")
            : out.kind === "MORE"
            ? t("{{amount}} more than due — it is given as an advance.", { amount: rupees(out.diff) })
            : t("{{amount}} less than due — it stays due for next time.", { amount: rupees(out.diff) })}
        </p>
        {out.kind === "MORE" ? (
          <>
            <label className="kr-label" htmlFor="kho-per">
              {t("Cut from each later payout (optional)")}
            </label>
            <input
              id="kho-per"
              className="kr-select"
              inputMode="decimal"
              value={perPay}
              placeholder={rupees(Number(line.agreedRecovery) || out.diff)}
              onChange={(e) => setPerPay(e.target.value.replace(/[^\d.]/g, ""))}
            />
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="kh-btn" onClick={onCancel}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="kh-btn is-primary"
          disabled={busy || !valid}
          onClick={() => onPay({ paid: String(givenN), cut: String(cutN), method, ...(out.kind === "MORE" && perPay ? { recoveryPerPay: perPay } : {}) })}
        >
          {busy ? t("Saving…") : t("Paid {{amount}}", { amount: rupees(givenN) })}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default HandOverDialog;
